import { Children, Fragment } from "react";
import { Box } from "../Box/Box";
import { type UseBoxStylesProps, useBoxStyles } from "../Box/useBoxStyles";
import { Divider, type DividerProps } from "../Divider/Divider";
import type { ReactNodeNoStrings } from "../private/ReactNodeNoStrings";
import { type Align, alignToFlexAlign } from "../utils/align";
import {
  mapResponsiveProp,
  type ResponsiveProp,
} from "../utils/responsiveProp";
import styles from "./Stack.module.css";

export interface UseStackProps {
  align: ResponsiveProp<Align>;
  component: UseBoxStylesProps["component"];
  space: UseBoxStylesProps["paddingBottom"];
}

const alignToDisplay = {
  left: "block",
  center: "flex",
  right: "flex",
} as const;

export const useStackItem = ({ align, component, space }: UseStackProps) => {
  return useBoxStyles({
    component,
    className: styles.stackItem,
    paddingBottom: space,
    // If we're aligned left across all screen sizes,
    // there's actually no alignment work to do.
    ...(align === "left"
      ? {}
      : {
          display: mapResponsiveProp(align, alignToDisplay),
          flexDirection: "column",
          alignItems: alignToFlexAlign(align),
        }),
  });
};

const validStackComponents = ["div", "ol", "ul"] as const;

export interface StackProps {
  component?: (typeof validStackComponents)[number];
  children: ReactNodeNoStrings;
  space: UseBoxStylesProps["padding"];
  align?: ResponsiveProp<Align>;
  dividers?: boolean | DividerProps["color"];
}

export const Stack = ({
  component = "div",
  children,
  space,
  align = "left",
  dividers = false,
}: StackProps) => {
  if (
    process.env.NODE_ENV === "development" &&
    !validStackComponents.includes(component)
  ) {
    throw new Error(`Invalid Stack component: ${component}`);
  }

  const stackClasses = useStackItem({ component, space, align });
  const stackItems = Children.toArray(children);

  const isList = component === "ol" || component === "ul";
  const stackItemComponent = isList ? "li" : "div";

  if (stackItems.length <= 1 && align === "left" && !isList) {
    return <Fragment>{stackItems}</Fragment>;
  }

  return (
    <Box component={component}>
      {stackItems.map((child, index) => (
        <Box
          component={stackItemComponent}
          className={dividers ? undefined : stackClasses}
          key={index}
        >
          {dividers && index > 0 ? (
            <Box width="full" paddingY={space}>
              {typeof dividers === "string" ? (
                <Divider color={dividers} />
              ) : (
                <Divider />
              )}
            </Box>
          ) : null}
          {child}
        </Box>
      ))}
    </Box>
  );
};
